import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { getApp } from 'firebase/app';
import {
  encryptBlob,
  decryptBlob,
  getBackupKey,
  getPathId,
} from './xmtpBackup';
import {
  ensureFirebaseAuth,
  uploadEncryptedFile,
  downloadEncryptedFile,
} from './firebaseStorage';
import type { ConversationData, ChatMessage } from '../context/chatTypes';

// ---- Types ----

interface ArchiveConvoMeta {
  id: string;
  topic: string;
  peerAddress: string;
  peerInboxId?: string;
  isGroup: boolean;
  groupName?: string;
  members?: string[];
  createdAt: string;
  lastMessageText?: string;
  lastMessageTime?: string;
  lastMessageSender?: string;
  messageCount: number;
  lastSyncedMessageId: string;
  lastSyncedAt: string;
  chunks: number;
}

interface ArchiveManifest {
  version: 1;
  updatedAt: string;
  conversations: Record<string, ArchiveConvoMeta>;
}

interface ArchiveMessageChunk {
  version: 1;
  conversationId: string;
  chunkIndex: number;
  messages: {
    id: string;
    senderAddress: string;
    content: any;
    contentType: string;
    sentAt: string;
    isSentByMe: boolean;
  }[];
}

// ---- Constants ----

const CHUNK_SIZE_LIMIT = 500 * 1024; // 500KB per chunk
const MANIFEST_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

// ---- In-memory manifest cache ----

let cachedManifest: ArchiveManifest | null = null;
let cachedManifestTime = 0;
let syncInProgress = false;

// ---- Helpers ----

async function conversationSlug(conversationId: string): Promise<string> {
  const data = new TextEncoder().encode(conversationId);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(hash);
  return Array.from(bytes.slice(0, 8))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function archiveBasePath(address: string): string {
  const pathId = getPathId(address);
  return `xmtp-backups/${pathId}/archive`;
}

async function encryptJson(obj: unknown, key: Uint8Array): Promise<Uint8Array> {
  const json = JSON.stringify(obj);
  const data = new TextEncoder().encode(json);
  const { payload, nonce } = await encryptBlob(data, key);
  const result = new Uint8Array(12 + payload.length);
  result.set(nonce, 0);
  result.set(payload, 12);
  return result;
}

async function decryptJson<T>(data: Uint8Array, key: Uint8Array): Promise<T> {
  const nonce = data.slice(0, 12);
  const payload = data.slice(12);
  const decrypted = await decryptBlob(payload, key, nonce);
  const json = new TextDecoder().decode(decrypted);
  return JSON.parse(json) as T;
}

/**
 * Try to download a file from Firebase Storage by path.
 * Returns null if file doesn't exist (storage/object-not-found).
 */
async function downloadStorageFile(path: string): Promise<Uint8Array | null> {
  try {
    const storage = getStorage(getApp());
    const storageRef = ref(storage, path);
    const url = await getDownloadURL(storageRef);
    return await downloadEncryptedFile(url);
  } catch {
    return null;
  }
}

// ---- Manifest operations ----

export async function downloadManifest(address: string): Promise<ArchiveManifest | null> {
  // Use cache if fresh
  if (cachedManifest && Date.now() - cachedManifestTime < MANIFEST_CACHE_TTL) {
    return cachedManifest;
  }

  const addr = address.toLowerCase();
  const key = await getBackupKey(addr);
  const path = `${archiveBasePath(addr)}/manifest.enc`;
  const data = await downloadStorageFile(path);
  if (!data || data.length < 13) return null;

  try {
    const manifest = await decryptJson<ArchiveManifest>(data, key);
    cachedManifest = manifest;
    cachedManifestTime = Date.now();
    return manifest;
  } catch (err: any) {
    console.warn('[Archive] Failed to decrypt manifest:', err?.message);
    return null;
  }
}

async function uploadManifest(address: string, manifest: ArchiveManifest): Promise<void> {
  const addr = address.toLowerCase();
  const key = await getBackupKey(addr);
  manifest.updatedAt = new Date().toISOString();
  const encrypted = await encryptJson(manifest, key);
  const path = `${archiveBasePath(addr)}/manifest.enc`;
  await uploadEncryptedFile(path, encrypted);
  cachedManifest = manifest;
  cachedManifestTime = Date.now();
}

// ---- Chunk operations ----

async function downloadChunk(
  address: string,
  slug: string,
  chunkIndex: number,
): Promise<ArchiveMessageChunk | null> {
  const addr = address.toLowerCase();
  const key = await getBackupKey(addr);
  const suffix = chunkIndex === 0 ? '' : `_${chunkIndex}`;
  const path = `${archiveBasePath(addr)}/msgs/${slug}${suffix}.enc`;
  const data = await downloadStorageFile(path);
  if (!data || data.length < 13) return null;

  try {
    return await decryptJson<ArchiveMessageChunk>(data, key);
  } catch {
    return null;
  }
}

async function uploadChunk(
  address: string,
  slug: string,
  chunk: ArchiveMessageChunk,
): Promise<void> {
  const addr = address.toLowerCase();
  const key = await getBackupKey(addr);
  const suffix = chunk.chunkIndex === 0 ? '' : `_${chunk.chunkIndex}`;
  const path = `${archiveBasePath(addr)}/msgs/${slug}${suffix}.enc`;
  const encrypted = await encryptJson(chunk, key);
  await uploadEncryptedFile(path, encrypted);
}

// ---- Per-conversation sync ----

async function syncConversationToArchive(
  address: string,
  convo: ConversationData,
  messages: ChatMessage[],
  manifest: ArchiveManifest,
): Promise<boolean> {
  if (!messages.length) return false;

  const slug = await conversationSlug(convo.id);
  const meta = manifest.conversations[slug];
  const lastSyncedId = meta?.lastSyncedMessageId;

  // Find new messages (after the last synced one)
  let newMessages: ChatMessage[];
  if (lastSyncedId) {
    const lastIdx = messages.findIndex((m) => m.id === lastSyncedId);
    if (lastIdx < 0) {
      // Last synced message not found in current messages — sync all
      newMessages = messages;
    } else {
      newMessages = messages.slice(lastIdx + 1);
    }
  } else {
    newMessages = messages;
  }

  if (newMessages.length === 0) return false;

  // Serialize new messages for the chunk
  const serialized = newMessages.map((m) => ({
    id: m.id,
    senderAddress: m.senderAddress,
    content: m.content,
    contentType: m.contentType,
    sentAt: m.sentAt instanceof Date ? m.sentAt.toISOString() : String(m.sentAt),
    isSentByMe: m.isSentByMe,
  }));

  // Download latest chunk (or create new one)
  const currentChunkIndex = meta?.chunks ? meta.chunks - 1 : 0;
  let chunk = await downloadChunk(address, slug, currentChunkIndex);

  if (!chunk) {
    chunk = {
      version: 1,
      conversationId: convo.id,
      chunkIndex: currentChunkIndex,
      messages: [],
    };
  }

  // Deduplicate: filter out messages already in the chunk
  const existingIds = new Set(chunk.messages.map((m) => m.id));
  const toAdd = serialized.filter((m) => !existingIds.has(m.id));
  if (toAdd.length === 0) return false;

  chunk.messages.push(...toAdd);

  // Check if chunk exceeds size limit
  const chunkSize = new TextEncoder().encode(JSON.stringify(chunk)).length;
  if (chunkSize > CHUNK_SIZE_LIMIT && chunk.messages.length > 1) {
    // Upload current full chunk, start a new one with overflow
    const splitPoint = Math.floor(chunk.messages.length * 0.8);
    const overflow = chunk.messages.splice(splitPoint);
    await uploadChunk(address, slug, chunk);

    const newChunk: ArchiveMessageChunk = {
      version: 1,
      conversationId: convo.id,
      chunkIndex: chunk.chunkIndex + 1,
      messages: overflow,
    };
    await uploadChunk(address, slug, newChunk);

    manifest.conversations[slug] = {
      ...convoToMeta(convo),
      messageCount: (meta?.messageCount || 0) + toAdd.length,
      lastSyncedMessageId: newMessages[newMessages.length - 1].id,
      lastSyncedAt: new Date().toISOString(),
      chunks: newChunk.chunkIndex + 1,
    };
  } else {
    await uploadChunk(address, slug, chunk);

    manifest.conversations[slug] = {
      ...convoToMeta(convo),
      messageCount: (meta?.messageCount || 0) + toAdd.length,
      lastSyncedMessageId: newMessages[newMessages.length - 1].id,
      lastSyncedAt: new Date().toISOString(),
      chunks: Math.max(1, currentChunkIndex + 1),
    };
  }

  return true;
}

function convoToMeta(convo: ConversationData): Omit<ArchiveConvoMeta, 'messageCount' | 'lastSyncedMessageId' | 'lastSyncedAt' | 'chunks'> {
  return {
    id: convo.id,
    topic: convo.topic,
    peerAddress: convo.peerAddress,
    peerInboxId: convo.peerInboxId,
    isGroup: convo.isGroup,
    groupName: convo.groupName,
    members: convo.members,
    createdAt: convo.createdAt instanceof Date ? convo.createdAt.toISOString() : String(convo.createdAt),
    lastMessageText: convo.lastMessageText,
    lastMessageTime: convo.lastMessageTime instanceof Date ? convo.lastMessageTime.toISOString() : convo.lastMessageTime ? String(convo.lastMessageTime) : undefined,
    lastMessageSender: convo.lastMessageSender,
  };
}

// ---- Main sync entry point ----

/**
 * Sync all in-memory conversations and messages to the encrypted archive.
 * Incremental: only uploads new messages since last sync.
 */
export async function syncMessageArchive(
  address: string,
  conversations: ConversationData[],
  allMessages: Record<string, ChatMessage[]>,
  walletAddress?: string,
): Promise<void> {
  if (syncInProgress) return;
  syncInProgress = true;

  try {
    const addr = address.toLowerCase();
    await ensureFirebaseAuth(walletAddress || addr);

    let manifest = await downloadManifest(addr);
    if (!manifest) {
      manifest = { version: 1, updatedAt: new Date().toISOString(), conversations: {} };
    }

    let dirty = false;
    for (const convo of conversations) {
      const messages = allMessages[convo.id];
      if (!messages || messages.length === 0) continue;

      try {
        const changed = await syncConversationToArchive(addr, convo, messages, manifest);
        if (changed) dirty = true;
      } catch (err: any) {
        console.warn(`[Archive] Failed to sync conversation ${convo.id}:`, err?.message);
      }
    }

    if (dirty) {
      await uploadManifest(addr, manifest);
      console.info('[Archive] Sync complete — manifest updated.');
    }
  } catch (err: any) {
    console.warn('[Archive] Sync failed:', err?.message);
  } finally {
    syncInProgress = false;
  }
}

// ---- Restore operations (merge-only) ----

/**
 * Restore the conversation list from the archive manifest.
 * Returns ConversationData[] for conversations stored in the archive.
 */
export async function restoreConversationList(
  address: string,
  walletAddress?: string,
): Promise<ConversationData[]> {
  const addr = address.toLowerCase();
  await ensureFirebaseAuth(walletAddress || addr);

  const manifest = await downloadManifest(addr);
  if (!manifest) return [];

  return Object.values(manifest.conversations).map((meta) => ({
    id: meta.id,
    topic: meta.topic,
    peerAddress: meta.peerAddress,
    peerInboxId: meta.peerInboxId,
    isGroup: meta.isGroup,
    groupName: meta.groupName,
    members: meta.members,
    createdAt: new Date(meta.createdAt),
    lastMessageText: meta.lastMessageText,
    lastMessageTime: meta.lastMessageTime ? new Date(meta.lastMessageTime) : undefined,
    lastMessageSender: meta.lastMessageSender,
  }));
}

/**
 * Restore all messages for a given conversation from the archive.
 * Downloads and decrypts all chunks, returns merged ChatMessage[].
 */
export async function restoreConversationMessages(
  address: string,
  conversationId: string,
  manifest?: ArchiveManifest | null,
  walletAddress?: string,
): Promise<ChatMessage[]> {
  const addr = address.toLowerCase();
  await ensureFirebaseAuth(walletAddress || addr);

  if (!manifest) {
    manifest = await downloadManifest(addr);
  }
  if (!manifest) return [];

  const slug = await conversationSlug(conversationId);
  const meta = manifest.conversations[slug];
  if (!meta) return [];

  const allMessages: ChatMessage[] = [];
  const totalChunks = Math.max(1, meta.chunks);

  for (let i = 0; i < totalChunks; i++) {
    try {
      const chunk = await downloadChunk(addr, slug, i);
      if (chunk) {
        for (const m of chunk.messages) {
          allMessages.push({
            id: m.id,
            conversationId,
            senderAddress: m.senderAddress,
            content: m.content,
            contentType: m.contentType,
            sentAt: new Date(m.sentAt),
            isSentByMe: m.isSentByMe,
          });
        }
      }
    } catch (err: any) {
      console.warn(`[Archive] Failed to download chunk ${i} for ${conversationId}:`, err?.message);
    }
  }

  // Sort by time ascending
  allMessages.sort((a, b) => a.sentAt.getTime() - b.sentAt.getTime());
  return allMessages;
}

/**
 * Merge archived conversations and messages into the current XMTP state.
 * Only fills in conversations/messages that XMTP doesn't have.
 *
 * Returns { conversations, messages } that should be dispatched into state.
 */
export async function mergeArchiveIntoState(
  address: string,
  currentConversations: ConversationData[],
  currentMessages: Record<string, ChatMessage[]>,
  walletAddress?: string,
): Promise<{
  missingConversations: ConversationData[];
  missingMessages: Record<string, ChatMessage[]>;
} | null> {
  const addr = address.toLowerCase();

  try {
    await ensureFirebaseAuth(walletAddress || addr);
  } catch {
    return null;
  }

  const manifest = await downloadManifest(addr);
  if (!manifest) return null;

  const currentIds = new Set(currentConversations.map((c) => c.id));
  const archivedConvos = Object.values(manifest.conversations);

  const missingConversations: ConversationData[] = [];
  const missingMessages: Record<string, ChatMessage[]> = {};

  for (const meta of archivedConvos) {
    if (!currentIds.has(meta.id)) {
      // Entire conversation is missing from XMTP
      missingConversations.push({
        id: meta.id,
        topic: meta.topic,
        peerAddress: meta.peerAddress,
        peerInboxId: meta.peerInboxId,
        isGroup: meta.isGroup,
        groupName: meta.groupName,
        members: meta.members,
        createdAt: new Date(meta.createdAt),
        lastMessageText: meta.lastMessageText,
        lastMessageTime: meta.lastMessageTime ? new Date(meta.lastMessageTime) : undefined,
        lastMessageSender: meta.lastMessageSender,
      });

      // Load all messages for this missing conversation
      try {
        const msgs = await restoreConversationMessages(addr, meta.id, manifest, walletAddress);
        if (msgs.length > 0) {
          missingMessages[meta.id] = msgs;
        }
      } catch (err: any) {
        console.warn(`[Archive] Failed to restore messages for ${meta.id}:`, err?.message);
      }
    } else {
      // Conversation exists — check if archive has older messages XMTP lost
      const xmtpMessages = currentMessages[meta.id] || [];
      if (xmtpMessages.length < meta.messageCount) {
        try {
          const archivedMsgs = await restoreConversationMessages(addr, meta.id, manifest, walletAddress);
          const xmtpIds = new Set(xmtpMessages.map((m) => m.id));
          const missing = archivedMsgs.filter((m) => !xmtpIds.has(m.id));
          if (missing.length > 0) {
            missingMessages[meta.id] = missing;
          }
        } catch (err: any) {
          console.warn(`[Archive] Failed to merge messages for ${meta.id}:`, err?.message);
        }
      }
    }
  }

  if (missingConversations.length === 0 && Object.keys(missingMessages).length === 0) {
    return null;
  }

  console.info(
    `[Archive] Merge: ${missingConversations.length} missing conversations, ` +
    `${Object.keys(missingMessages).length} conversations with missing messages.`,
  );
  return { missingConversations, missingMessages };
}

/** Invalidate the in-memory manifest cache (e.g. after a fresh restore). */
export function invalidateManifestCache(): void {
  cachedManifest = null;
  cachedManifestTime = 0;
}
